import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { guardarInsumo, obtenerCategoriasInsumos, guardarCategoriaInsumo } from '../../services/insumosServicio';

const UNIDADES_MEDIDA = [
    { valor: 'UN', nombre: 'Unidad' },
    { valor: 'KG', nombre: 'Kilogramo (kg)' },
    { valor: 'G', nombre: 'Gramo (g)' },
    { valor: 'L', nombre: 'Litro (l)' },
    { valor: 'ML', nombre: 'Mililitro (ml)' },
];

const EMPTY_INSUMO = {
    nombre: '', categoria_id: '', costo: '', codigo_barras: '',
    controlar_stock: false, stock: 0, stock_minimo: 0, unidad_medida: 'UN'
};

const getInitialState = (insumo) => {
    if (!insumo) return { ...EMPTY_INSUMO };
    return {
        nombre: insumo.nombre || '',
        categoria_id: insumo.categoria_id || '',
        costo: insumo.costo || '',
        codigo_barras: insumo.codigo_barras || '',
        controlar_stock: Boolean(insumo.controlar_stock),
        stock: insumo.stock || 0,
        stock_minimo: insumo.stock_minimo || 0,
        unidad_medida: insumo.unidad_medida || 'UN',
    };
};

const InsumoFormModal = ({ isVisible, onClose, onSave, insumoToEdit }) => {
    const [insumo, setInsumo] = useState(getInitialState(insumoToEdit));
    const [categorias, setCategorias] = useState([]);
    const [saving, setSaving] = useState(false);
    const [nuevaCategoria, setNuevaCategoria] = useState('');
    const [mostrarNuevaCategoria, setMostrarNuevaCategoria] = useState(false);

    const cargarCategorias = () => {
        obtenerCategoriasInsumos().then(setCategorias).catch(() => setCategorias([]));
    };

    useEffect(() => {
        if (isVisible) {
            setInsumo(getInitialState(insumoToEdit));
            setMostrarNuevaCategoria(false);
            setNuevaCategoria('');
            cargarCategorias();
        }
    }, [insumoToEdit, isVisible]);

    const handleChange = (campo, valor) => {
        setInsumo(prev => ({ ...prev, [campo]: valor }));
    };

    const handleCrearCategoria = async () => {
        if (!nuevaCategoria.trim()) return;
        try {
            const resp = await guardarCategoriaInsumo({ nombre: nuevaCategoria.trim() });
            cargarCategorias();
            if (resp && resp.id) handleChange('categoria_id', resp.id);
            setNuevaCategoria('');
            setMostrarNuevaCategoria(false);
            toast.success("Categoría creada");
        } catch (err) {
            toast.error("No se pudo crear la categoría");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!insumo.nombre) return toast.warning("Debes asignar un nombre");

        setSaving(true);
        try {
            await guardarInsumo({
                id: insumoToEdit ? insumoToEdit.id : undefined,
                nombre: insumo.nombre,
                categoria_id: insumo.categoria_id || null,
                costo: insumo.costo || 0,
                codigo_barras: insumo.codigo_barras || '',
                controlar_stock: !!insumo.controlar_stock,
                stock: insumo.stock || 0,
                stock_minimo: insumo.stock_minimo || 0,
                unidad_medida: insumo.unidad_medida || 'UN',
            });
            onSave();
        } catch (err) {
            toast.error(err.message || "Error al guardar el insumo");
        } finally {
            setSaving(false);
        }
    };

    if (!isVisible) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-2xl rounded-xl overflow-hidden">
                    <div className="modal-header bg-white border-0 pt-4 px-4">
                        <h5 className="font-weight-bold m-0">{insumoToEdit ? 'Editar Insumo' : 'Nuevo Insumo'}</h5>
                        <button className="close" onClick={onClose}>&times;</button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4">
                            <div className="form-group mb-3">
                                <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Nombre del insumo</label>
                                <input
                                    type="text" className="form-control form-control-lg border-light bg-light"
                                    value={insumo.nombre} onChange={(e) => handleChange('nombre', e.target.value)}
                                    placeholder="Ej. Harina, Queso mozzarella, Vaso 12oz"
                                />
                            </div>

                            <div className="form-group mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-0">Categoría</label>
                                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setMostrarNuevaCategoria(!mostrarNuevaCategoria)}>
                                        + nueva categoría
                                    </button>
                                </div>
                                <select className="form-control bg-light border-light" value={insumo.categoria_id} onChange={(e) => handleChange('categoria_id', e.target.value)}>
                                    <option value="">Sin categoría</option>
                                    {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                                </select>
                                {mostrarNuevaCategoria && (
                                    <div className="input-group mt-2">
                                        <input
                                            type="text" className="form-control"
                                            placeholder="Nombre de la nueva categoría"
                                            value={nuevaCategoria}
                                            onChange={(e) => setNuevaCategoria(e.target.value)}
                                        />
                                        <div className="input-group-append">
                                            <button type="button" className="btn btn-outline-primary" onClick={handleCrearCategoria}>Crear</button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="row">
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Costo</label>
                                    <input
                                        type="number" min="0" className="form-control bg-light border-light"
                                        value={insumo.costo} onChange={(e) => handleChange('costo', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-6 form-group mb-3">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Código/SKU</label>
                                    <input
                                        type="text" className="form-control bg-light border-light"
                                        value={insumo.codigo_barras} onChange={(e) => handleChange('codigo_barras', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="form-group mb-3">
                                <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Unidad de medida</label>
                                <select
                                    className="form-control bg-light border-light"
                                    value={insumo.unidad_medida}
                                    onChange={(e) => handleChange('unidad_medida', e.target.value)}
                                >
                                    {UNIDADES_MEDIDA.map(u => <option key={u.valor} value={u.valor}>{u.nombre}</option>)}
                                </select>
                                <small className="text-muted">En qué unidad llevas el stock de este insumo (kg, litros, etc.). Es solo referencia visual, no convierte unidades.</small>
                            </div>

                            <div className="bg-light p-3 rounded-lg">
                                <div className="custom-control custom-switch mb-2">
                                    <input
                                        type="checkbox" className="custom-control-input" id="swControlarStock"
                                        checked={insumo.controlar_stock}
                                        onChange={(e) => handleChange('controlar_stock', e.target.checked)}
                                    />
                                    <label className="custom-control-label font-weight-bold text-dark" htmlFor="swControlarStock">
                                        Controlar stock de este insumo
                                    </label>
                                </div>

                                {insumo.controlar_stock && (
                                    <div className="row mt-3">
                                        <div className="col-6">
                                            <label className="small text-muted">Stock actual</label>
                                            <input
                                                type="number" min="0" step="0.001" className="form-control"
                                                value={insumo.stock} onChange={(e) => handleChange('stock', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="small text-muted">Stock mínimo</label>
                                            <input
                                                type="number" min="0" step="0.001" className="form-control"
                                                value={insumo.stock_minimo} onChange={(e) => handleChange('stock_minimo', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer border-0 bg-white p-4">
                            <button type="button" className="btn btn-link text-muted" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn btn-primary btn-lg rounded-pill px-5 shadow-sm" disabled={saving}>
                                {saving ? 'Guardando...' : 'Guardar Insumo'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default InsumoFormModal;
